/**
 * Centralized error handling utilities for the application.
 * Provides user-friendly error messages, error categorization, and logging.
 */

import { supabase } from "@/integrations/supabase/client";

export type ErrorCategory = 
  | "exa_api" 
  | "gemini_api" 
  | "firecrawl" 
  | "rate_limit" 
  | "network" 
  | "auth" 
  | "credits"
  | "content_length"
  | "timeout"
  | "browser_compat"
  | "unknown";

export interface ParsedError {
  category: ErrorCategory;
  userMessage: string;
  technicalMessage: string;
  isRetryable: boolean;
  suggestedAction?: string;
  creditProtected: boolean;
}

export const ERROR_MESSAGES: Record<ErrorCategory, Omit<ParsedError, 'technicalMessage'>> = {
  exa_api: {
    category: "exa_api",
    userMessage: "We couldn't fetch trending data right now. Your content will still be generated using our AI.",
    isRetryable: true,
    suggestedAction: "Try again later for trend-enhanced results.",
    creditProtected: true
  },
  gemini_api: {
    category: "gemini_api",
    userMessage: "Our AI is temporarily unavailable. Please try again in a few moments.",
    isRetryable: true,
    creditProtected: true
  },
  firecrawl: {
    category: "firecrawl",
    userMessage: "We couldn't analyze that website. Please check the URL is correct and publicly accessible.",
    isRetryable: true,
    suggestedAction: "Try the 'I Don't Have a Website' option instead.",
    creditProtected: true
  },
  rate_limit: {
    category: "rate_limit",
    userMessage: "You're generating content too quickly. Please wait a moment and try again.",
    isRetryable: true,
    suggestedAction: "Wait 30 seconds before retrying.",
    creditProtected: true
  },
  network: {
    category: "network",
    userMessage: "Connection issue. Please check your internet and try again.",
    isRetryable: true,
    creditProtected: true
  },
  auth: {
    category: "auth",
    userMessage: "Your session has expired. Please refresh the page and try again.",
    isRetryable: false,
    creditProtected: true
  },
  credits: {
    category: "credits",
    userMessage: "Don't worry - no credits were used since generation failed.",
    isRetryable: true,
    creditProtected: true
  },
  content_length: {
    category: "content_length",
    userMessage: "The content was too long and got cut off. Please try with a shorter input.",
    isRetryable: true,
    suggestedAction: "Try generating with a shorter transcript or topic.",
    creditProtected: true
  },
  timeout: {
    category: "timeout",
    userMessage: "The request timed out. Your credit has been refunded. Please try again.",
    isRetryable: true,
    suggestedAction: "Try a simpler prompt or wait a moment and retry.",
    creditProtected: true
  },
  browser_compat: {
    category: "browser_compat",
    userMessage: "Your browser may be outdated. Please update to the latest version for the best experience.",
    isRetryable: false,
    suggestedAction: "Update your browser to the latest version (Safari 14+, Chrome 80+, Firefox 78+).",
    creditProtected: true
  },
  unknown: {
    category: "unknown",
    userMessage: "Something went wrong. Please try again.",
    isRetryable: true,
    creditProtected: true
  }
};

/**
 * Parse an error and return a user-friendly error object with categorization.
 */
export function parseError(error: unknown): ParsedError {
  const message = (error as Error)?.message?.toLowerCase() || "";
  const status = (error as { status?: number })?.status || 
                 (error as { context?: { status?: number } })?.context?.status;
  
  let category: ErrorCategory = "unknown";
  let technicalMessage = (error as Error)?.message || String(error);
  
  // Rate limit detection
  if (status === 429 || message.includes("rate limit") || message.includes("too many") || message.includes("too quickly")) {
    category = "rate_limit";
  }
  // Browser compatibility errors (matchMedia.addEventListener, ResizeObserver, etc.)
  else if (message.includes("addeventlistener is not a function") || message.includes("addlistener") || message.includes("resizeobserver") || message.includes("matchmedia") || message.includes("is not a function") && (message.includes("addeventlistener") || message.includes("observe"))) {
    category = "browser_compat";
  }
  // Network errors (including Supabase FunctionsFetchError)
  else if (message.includes("failed to send") || message.includes("functionsfetcherror") || message.includes("networkerror") || message.includes("failed to fetch") || message.includes("network") || message.includes("connection")) {
    category = "network";
  }
  // Firecrawl/scraping errors
  else if (message.includes("scrape") || message.includes("firecrawl") || message.includes("blocked") || message.includes("couldn't analyze")) {
    category = "firecrawl";
  }
  // Content length errors
  else if (message.includes("too long") || message.includes("length") || message.includes("cut off") || message.includes("truncated")) {
    category = "content_length";
  }
  // Exa API errors
  else if (message.includes("exa") || message.includes("market insight") || message.includes("trending")) {
    category = "exa_api";
  }
  // AI/Gemini errors
  else if (message.includes("ai") || message.includes("gemini") || message.includes("gateway") || status === 500) {
    category = "gemini_api";
  }
  // Auth errors
  else if (status === 401 || message.includes("auth") || message.includes("token") || message.includes("session")) {
    category = "auth";
  }
  
  const errorTemplate = ERROR_MESSAGES[category];
  
  return {
    ...errorTemplate,
    technicalMessage
  };
}

/**
 * Parse error response from edge functions that include errorCategory.
 */
export function parseEdgeFunctionError(response: { 
  error?: string; 
  errorCategory?: ErrorCategory; 
  creditProtected?: boolean;
  technicalDetails?: string;
}): ParsedError {
  if (response.errorCategory && ERROR_MESSAGES[response.errorCategory]) {
    const template = ERROR_MESSAGES[response.errorCategory];
    return {
      ...template,
      technicalMessage: response.technicalDetails || response.error || "Unknown error",
      userMessage: response.error || template.userMessage
    };
  }
  
  // Fall back to parsing the error message
  return parseError(new Error(response.error || "Unknown error"));
}

/**
 * Log an error for monitoring and debugging.
 * Sends error to database asynchronously (fire-and-forget).
 */
export function logError(
  category: ErrorCategory,
  action: string,
  error: unknown,
  userId?: string
): void {
  // ── Skip logging for local dev environments ──
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  if (currentUrl.includes('localhost') || currentUrl.includes('127.0.0.1')) {
    console.warn('[Error Log] Skipped — dev environment:', (error as Error)?.message || String(error));
    return;
  }

  const message = (error as Error)?.message || String(error);
  const messageLower = message.toLowerCase();

  // ── Auto-recategorize timeout/abort errors as expected behavior ──
  // "signal is aborted without reason" = user clicked Stop or 120s limit hit.
  // These are expected and credit is auto-refunded, so categorize as timeout.
  let resolvedCategory = category;
  if (
    messageLower.includes('signal is aborted') ||
    messageLower.includes('aborted without reason') ||
    messageLower.includes('the operation was aborted') ||
    messageLower.includes('aborterror')
  ) {
    resolvedCategory = 'timeout';
  }

  const errorLog = {
    category: resolvedCategory,
    action,
    message,
    stack: (error as Error)?.stack,
    status: (error as { status?: number })?.status,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    url: currentUrl || undefined,
  };
  
  // Console log for immediate debugging
  console.error("[Error Log]", JSON.stringify(errorLog, null, 2));
  
  // Fire-and-forget database logging (don't await)
  supabase.functions.invoke("log-error", {
    body: errorLog,
  }).catch((err) => {
    // Silently fail - we don't want error logging to cause more errors
    console.warn("Failed to log error to database:", err);
  });
}

/**
 * Get the credit protection message to display to users when an error occurs.
 */
export function getCreditProtectionMessage(): string {
  return "Don't worry - no credits were used since generation failed.";
}

/**
 * Check if an error response indicates the user should retry.
 */
export function shouldRetry(error: ParsedError): boolean {
  return error.isRetryable && error.category !== "auth";
}
